import { useState } from "react";
import { Avatar, AvatarImage } from "./avatar";
import { Button } from "./button";
import { Separator } from "./separator";
import { AccountDialog } from "./account-dialog";
import { TeamChannelDialog } from "./team-channel-dialog";
import {
  ChevronsUpDown,
  LayoutDashboard,
  Sparkles,
  UsersRound,
  Ticket,
  Inbox,
  ChevronDown,
  ChevronRight,
  Plus,
  MoreHorizontal,
  LucideIcon,
} from "lucide-react";
import i18n from "@tellescope/i18n";
import { useTranslation } from "react-i18next";

export interface SidebarNavigationItem {
  id: string;
  icon?: LucideIcon;
  label: string;
  path?: string;
  children?: SidebarNavigationItem[];
  badge?: string | number;
  emoji?: string;
}

export interface SidebarUser {
  name: string;
  email: string;
  avatar?: string;
}

export interface AppSidebarProps {
  activePage?: string;
  onNavigate?: (path: string) => void;
  onLogout?: () => void | Promise<void>;
  user?: SidebarUser;
  navigationItems?: SidebarNavigationItem[];
  showMessagingSection?: boolean;
  messagingItems?: SidebarNavigationItem[];
  teamChannels?: SidebarNavigationItem[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
  onCreateTeamChannel?: (data: {
    name: string;
    description: string;
    participants: string[];
    isPublic: boolean;
  }) => void | Promise<void>;
  availableUsers?: { id: string; name: string; email?: string; avatar?: string }[];
  customDashboardContent?: React.ReactNode;
}

const DEFAULT_USER: SidebarUser = {
  name: "Tea",
  email: "m@example.com",
};

const DEFAULT_NAVIGATION: SidebarNavigationItem[] = [
  {
    id: "dashboard",
    icon: LayoutDashboard,
    label: i18n.t("Dashboard", { ns: "sidebar" }),
    path: "/dashboard",
  },
  { id: "ai", icon: Sparkles, label: i18n.t("Ask AI", { ns: "sidebar" }), path: "/ai" },
  { id: "contacts", icon: UsersRound, label: i18n.t("Contacts", { ns: "sidebar" }), path: "/contacts" },
  { id: "tickets", icon: Ticket, label: i18n.t("Tickets", { ns: "sidebar" }), path: "/tickets" },
];

const DEFAULT_MESSAGING_ITEMS: SidebarNavigationItem[] = [
  { id: "inbox", label: i18n.t("Inbox", { ns: "sidebar" }), badge: 128 },
  { id: "drafts-sent", label: i18n.t("Drafts & Sent", { ns: "sidebar" }), badge: 123 },
  { id: "archive", label: i18n.t("Archive", { ns: "sidebar" }) },
  { id: "trash", label: i18n.t("Trash", { ns: "sidebar" }) },
];

const DEFAULT_TEAM_CHANNELS: SidebarNavigationItem[] = [
  { id: "add-channel", icon: Plus, label: i18n.t("Add a team channel", { ns: "sidebar" }) },
  { id: "tickets-channel", emoji: "📊", label: i18n.t("Tickets & such", { ns: "sidebar" }) },
  { id: "data-channel", emoji: "🍳", label: i18n.t("Data cleanliness", { ns: "sidebar" }) },
  { id: "service-channel", emoji: "💪", label: i18n.t("Customer service", { ns: "sidebar" }) },
  { id: "resources-channel", emoji: "📚", label: i18n.t("Resources", { ns: "sidebar" }) },
];

/** @deprecated - Please use app-specific AppSidebar until this abstraction is fixed */
export function AppSidebar({
  activePage,
  onNavigate,
  onLogout,
  user = DEFAULT_USER,
  navigationItems = DEFAULT_NAVIGATION,
  showMessagingSection = true,
  messagingItems = DEFAULT_MESSAGING_ITEMS,
  teamChannels = DEFAULT_TEAM_CHANNELS,
  isCollapsed = false,
  onToggleCollapse: _onToggleCollapse,
  className = "",
  onCreateTeamChannel,
  availableUsers = [],
  customDashboardContent,
}: AppSidebarProps) {
  const { t } = useTranslation("sidebar");
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isTeamChannelDialogOpen, setIsTeamChannelDialogOpen] = useState(false);
  const [isMessagingExpanded, setIsMessagingExpanded] = useState(true);

  const handleNavigation = (path: string) => {
    onNavigate?.(path);
  };

  const handleAccountClick = () => {
    setIsAccountDialogOpen(true);
  };

  const renderNavigationItem = (item: SidebarNavigationItem, isChild = false) => {
    const isActive = activePage === item.id;
    const hasChildren = item.children && item.children.length > 0;

    // Handle special case for "add team channel" button
    if (item.id === "add-channel") {
      return (
        <Button
          key={item.id}
          variant="ghost"
          className={`w-full h-${isChild ? "9" : "8"} px-${isChild ? "3" : "2"} py-2 rounded-md ${
            isCollapsed ? "justify-center" : "justify-start"
          }`}
          onClick={() => setIsTeamChannelDialogOpen(true)}
          title={isCollapsed ? item.label : ""}
        >
          {item.icon && <item.icon className={`h-4 w-4 text-sidebar-foreground ${!isCollapsed ? "mr-2" : ""}`} />}
          {!isCollapsed && (
            <span className={`text-sm ${isChild ? "font-medium" : ""} text-sidebar-foreground`}>{item.label}</span>
          )}
        </Button>
      );
    }

    return (
      <Button
        key={item.id}
        variant="ghost"
        className={`w-full h-${isChild ? "9" : "8"} px-${isChild ? "3" : "2"} py-2 rounded-md ${
          isCollapsed ? "justify-center" : "justify-start"
        } ${
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            : ""
        }`}
        onClick={item.path ? () => handleNavigation(item.path!) : hasChildren ? () => {} : undefined}
        title={isCollapsed ? item.label : ""}
      >
        {item.icon && <item.icon className={`h-4 w-4 text-sidebar-foreground ${!isCollapsed ? "mr-2" : ""}`} />}
        {item.emoji && !isCollapsed && <span className="mr-2">{item.emoji}</span>}
        {!isCollapsed && (
          <>
            <span className={`text-sm ${isChild ? "font-medium" : ""} text-sidebar-foreground`}>{item.label}</span>
            {item.badge && <span className="ml-auto text-sm font-medium">{item.badge}</span>}
            {hasChildren && <MoreHorizontal className="ml-auto h-4 w-4 text-sidebar-foreground" />}
            {item.id === "messaging" &&
              (isMessagingExpanded ? (
                <ChevronDown className="ml-auto h-4 w-4 text-sidebar-foreground" />
              ) : (
                <ChevronRight className="ml-auto h-4 w-4 text-sidebar-foreground" />
              ))}
          </>
        )}
      </Button>
    );
  };

  return (
    <>
      <div
        className={`absolute left-0 top-0 h-screen bg-sidebar text-sidebar-foreground border-r transition-all duration-300 z-50 ${
          isCollapsed ? "w-16" : "w-[254px]"
        } ${className}`}
      >
        {/* User Section */}
        <div className="px-2 py-2 border-b h-[52px] flex items-center">
          <Button
            variant="ghost"
            className={`flex items-center gap-2 p-2 rounded-md w-full hover:bg-sidebar-accent ${
              isCollapsed ? "justify-center" : "justify-start"
            }`}
            onClick={handleAccountClick}
            title={isCollapsed ? user.name : ""}
          >
            <div className="flex items-start">
              <Avatar className="h-8 w-8 rounded-lg bg-background">
                <AvatarImage src={user.avatar || "https://github.com/shadcn.png"} alt={user.name} />
              </Avatar>
            </div>
            {!isCollapsed && (
              <>
                <div className="flex flex-col justify-center items-start gap-0.5 flex-1">
                  <p className="text-sm font-semibold text-sidebar-foreground leading-none">{user.name}</p>
                  <p className="text-xs text-sidebar-foreground leading-none">{user.email}</p>
                </div>
                <div className="flex items-center justify-center">
                  <ChevronsUpDown className="h-4 w-4 text-sidebar-foreground" />
                </div>
              </>
            )}
          </Button>
        </div>

        {/* Main Navigation */}
        <div className="pt-2 px-2">
          <div className="space-y-1">
            {navigationItems.map((item) => renderNavigationItem(item))}

            {showMessagingSection && (
              <Button
                variant="ghost"
                className={`w-full h-8 px-2 py-2 rounded-md ${isCollapsed ? "justify-center" : "justify-start"}`}
                onClick={() => setIsMessagingExpanded(!isMessagingExpanded)}
                title={isCollapsed ? "Messaging" : ""}
              >
                <Inbox className={`h-4 w-4 text-sidebar-foreground ${!isCollapsed ? "mr-2" : ""}`} />
                {!isCollapsed && (
                  <>
                    <span className="text-sm text-sidebar-foreground">{t("Messaging")}</span>
                    {isMessagingExpanded ? (
                      <ChevronDown className="ml-auto h-4 w-4 text-sidebar-foreground" />
                    ) : (
                      <ChevronRight className="ml-auto h-4 w-4 text-sidebar-foreground" />
                    )}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Messaging Section */}
        {showMessagingSection && isMessagingExpanded && !isCollapsed && (
          <>
            <div className="px-2 py-2">
              <div className="space-y-1">{messagingItems.map((item) => renderNavigationItem(item, true))}</div>
            </div>

            <div className="px-2">
              <Separator />
            </div>

            <div className="px-2 py-2">
              <div className="space-y-1">{teamChannels.map((item) => renderNavigationItem(item))}</div>
            </div>
          </>
        )}
      </div>

      <AccountDialog
        open={isAccountDialogOpen}
        onOpenChange={setIsAccountDialogOpen}
        defaultExpanded={false}
        activeSection="Account"
        showTrigger={false}
        onLogout={onLogout}
        customDashboardContent={customDashboardContent}
      />

      <TeamChannelDialog
        open={isTeamChannelDialogOpen}
        onOpenChange={setIsTeamChannelDialogOpen}
        defaultExpanded={false}
        showTrigger={false}
        availableUsers={availableUsers}
        onCreateChannel={onCreateTeamChannel}
      />
    </>
  );
}
